import { ActionIcon, Avatar, Group, Stack, Table, Text, Tooltip } from "@mantine/core";
import { RotateCw, Trash2 } from "lucide-react";

import type { Invitation } from "../../api/generated/model";
import { ROLE_LABEL } from "../permissions";
import { breakableEmail } from "./email";

export interface PendingRowProps {
  invitation: Invitation;
  /** This role may send the invitation's role, so it may resend or revoke it (A2). */
  canManage: boolean;
  resending: boolean;
  onResend: () => void;
  onRevoke: () => void;
}

/**
 * A pending invitation as a row of the members table, as live shows one (`member-cell` and
 * `actions-cell`; spec teams/invite-by-email): live's words, and its tooltips on the actions.
 */
export function PendingRow({ invitation, canManage, resending, onResend, onRevoke }: PendingRowProps) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          {/* Decorative, as on a member's row. */}
          <Avatar size={38} radius={8} color="indigo" name={invitation.email} aria-hidden />
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              {breakableEmail(invitation.email)}
            </Text>
            {/* Live's warning line; live never says that an invitation expired. */}
            <Text size="xs" fw={500} c="var(--cai-color-text-warning)">
              {invitation.expired ? "Invite expired" : "Pending invite"}
            </Text>
          </Stack>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{breakableEmail(invitation.email)}</Text>
      </Table.Td>
      {/* Live disables a pending row's role select: the role is text until they join. */}
      <Table.Td>{ROLE_LABEL[invitation.role]}</Table.Td>
      <Table.Td align="right">
        {canManage ? (
          <Group gap={6} wrap="nowrap" justify="flex-end">
            <Tooltip label="Resend invite">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Resend invite to ${invitation.email}`}
                loading={resending}
                onClick={onResend}
              >
                <RotateCw size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Remove member">
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={`Revoke the invitation to ${invitation.email}`}
                onClick={onRevoke}
              >
                <Trash2 size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ) : null}
      </Table.Td>
    </Table.Tr>
  );
}
